import { useAuth, useSession } from "@clerk/clerk-react";
import Loading from "../components/Loading";
import useGqlClient from "../hooks/useGqlClient";
import {
  AccountStatus,
  CreateAccountSource,
  useCreateAccountMutation,
} from "../graphql/generated";
import { useEffect } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { actions } from "../store/auth/slice";
import { SignupSource } from "./Signup";
import { authSelectors } from "../store/auth/selector";
import { Card } from "../components/Card";
import Logo from "../components/Logo";
import { Button } from "../components/tailwind/Button";
import { usePostHog } from "posthog-js/react";
import { useQueryClient } from "@tanstack/react-query";

export function OnboardingGate(props: { children: React.ReactNode }) {
  const { session, isSignedIn } = useSession();
  const { signOut } = useAuth();
  const dispatch = useDispatch();
  const history = useHistory();

  const posthog = usePostHog();
  const queryClient = useQueryClient();

  const account = useSelector(authSelectors.account);

  if (isSignedIn === false) {
    return <Redirect to={"/login"} />;
  }

  if (!session) {
    return (
      <div className="flex flex-1 justify-center items-center">
        <Loading />
      </div>
    );
  }

  if (account && account.status == AccountStatus.Pending) {
    return (
      <div className="flex justify-center items-center h-screen flex-1 bg-gradient-to-br from-persian-950 to-persian-800">
        <div>
          <Card padding="l">
            <Logo mode="light" width={120} height={30} />
            <div className="mt-3">
              <p className="font-semibold text-gray-900">
                Account is awaiting approval
              </p>
              <p className="text-gray-600 text-sm mt-2">
                Thank you for your interest in Liquid! We didn't recognise your
                account, so you need to go through our standard screening and
                onboarding process to get access to the platform.
              </p>

              <p className="text-gray-600 text-sm">
                Someone from our team will reach out to you within the next 24
                hours.
              </p>

              <p className="text-gray-600 text-sm mt-3">
                If this was a mistake, or you need help more urgently, please
                contact{" "}
                <a
                  href="mailto:support@liquidacquire.com"
                  className="font-semibold text-persian-500"
                >
                  support@liquidacquire.com
                </a>
                .
              </p>
            </div>
            <Button
              variant="danger"
              size="xs"
              text="Logout"
              margin="l 0 0 0"
              onClick={async () => {
                await signOut();
                posthog?.reset();

                dispatch(actions.logout());
                queryClient.removeQueries();
                queryClient.invalidateQueries();
                queryClient.invalidateQueries({
                  queryKey: ["Account", {}],
                });
                history.push("/login");
              }}
            />
          </Card>
        </div>
      </div>
    );
  }

  if (account) {
    return <>{props.children}</>;
  }

  const signupSource =
    (session.user.unsafeMetadata.signupSource as SignupSource) ??
    SignupSource.signupPage;

  console.log(signupSource)

  return (
    <OnboardingStatus
      clerkUserId={session.user.id}
      onboardingStatus={
        session.user.unsafeMetadata.onboarding as OnboardingStatuses
      }
      signupSource={signupSource}
    />
  );
}

type OnboardingStatuses = "initial" | "completed" | "error";

function OnboardingStatus(props: {
  onboardingStatus: OnboardingStatuses;
  signupSource: SignupSource;
  clerkUserId: string;
  token?: string;
}) {
  const client = useGqlClient();

  const { session } = useSession();
  const dispatch = useDispatch();
  const history = useHistory();
  const createAccount = useCreateAccountMutation(client);

  useEffect(() => {
    if (createAccount.isPending) {
      return;
    }

    const isSellerSignup = props.signupSource === SignupSource.sellerSignup;

    createAccount.mutate(
      {
        clerkUserId: props.clerkUserId,
        source: isSellerSignup ? CreateAccountSource.Tte : undefined,
      },
      {
        onSuccess: (data) => {
          dispatch(actions.refreshAccount({ account: data.createAccount }));

          if (
            !isSellerSignup &&
            !data.createAccount.firm &&
            data.createAccount.deals.length === 0 &&
            props.signupSource === SignupSource.signupPage
          ) {
            history.push("/onboarding/create-firm");
          } else {
            history.push("/");
          }

          if (session) {
            session.reload();
          }
        },
      },
    );
  }, [props.clerkUserId]);

  return (
    <div className="flex flex-1 w-full  justify-center items-center">
      <Loading />
    </div>
  );
}
